import React, {useCallback, useEffect, useState, useMemo} from "react";
import {Button, Card} from "react-bootstrap";
import {Location, Photo, User} from "../../types/Types";
import {collection, doc, getDoc, getDocs, limit, orderBy, query, where} from "@firebase/firestore";
import {db} from "../../../firebase/FirebaseProvider";
import UserDetails from "../UserDetails";
import UserDetailsV2 from "../UserDetailsV2";
import UserNav from "../UserNav";
import PhotoRow from "../../locations/profile/PhotoRow";
import {PHOTOS_LIMIT} from "../../util/Util";
import PageTracking from "../../auth/PageTracking";
import {Link, useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHeart, faCalendar} from "@fortawesome/free-solid-svg-icons";
import {V2GlassPanel} from "../../shared/v2/V2Glass";
import V2PhotoRow from "../../shared/v2/V2PhotoRow";

interface Props {
    currentUser: User | undefined
    likesMap: Map<string, boolean>
    locations: Array<Location> | undefined
}

function UserPhotos(props: Props) {
    const { id } = useParams<{ id: string }>();

    const [user, setUser] = useState<User | undefined>(undefined);
    const [photos, setPhotos] = useState<Array<Photo> | undefined>(undefined);
    const [sortType, setSortType] = useState<string>('date');
    const isUiV2 = !!props.currentUser?.enableUiV2;

    const updatePhotoLikeCount = useCallback((photoId: string, adjustment: number) => {
        setPhotos(prevPhotos => {
            if (!prevPhotos) return prevPhotos;
            return prevPhotos.map(photo => {
                if (photo.id === photoId) {
                    return {
                        ...photo,
                        likeCount: (photo.likeCount || 0) + adjustment
                    };
                }
                return photo;
            });
        });
    }, []);

    const loadPhotos = useCallback(async() => {
        if(!user) {
            return;
        }

        const loadedPhotos: Array<Photo> = [];
        const photosOnceCollection = query(collection(db, 'photos'),
            where('userId', '==', user.id),
            where('hidden', '==', false),
            orderBy('createdAt', 'desc'),
            limit(PHOTOS_LIMIT));
        const photosCollectionSnapshot = await getDocs(photosOnceCollection);
        photosCollectionSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data) {
                const tempPhoto: Photo = { ...data, id: doc.id } as Photo;
                loadedPhotos.push(tempPhoto);
            }
        });
        console.log(`Loaded user photos: ${loadedPhotos.length} documents`);
        setPhotos(loadedPhotos);
    }, [user]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            const userRef = doc(db, 'users', id);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                const userToSet: User = { ...data, id: docSnap.id } as User;
                setUser(userToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!user) return;
        loadPhotos();
    }, [user, loadPhotos]);

    const handleSort = (newSortType: string) => {
        setSortType(newSortType);
    };

    // Sort photos based on sortType
    const displayPhotos = useMemo(() => {
        if (!photos) return undefined;
        
        // Create a shallow copy to avoid mutating the original array
        const photosToSort = [...photos];
        
        // Sort based on sortType
        if(sortType === 'likes') {
            return photosToSort.sort((a: Photo, b: Photo) => (b.likeCount || 0) - (a.likeCount || 0));
        } else {
            // Default sort by date (newest first)
            return photosToSort.sort((a: Photo, b: Photo) => 
                new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
        }
    }, [photos, sortType]);

    const v2DetailActions = (
        <>
            <button
                className={`user-v2-detail-stat user-v2-detail-stat-button user-v2-photo-sort-button${sortType === "date" ? " user-v2-detail-stat-button-active" : ""}`}
                disabled={sortType === "date"}
                type="button"
                onClick={() => handleSort("date")}
                aria-label="Sort photos by date"
            >
                <FontAwesomeIcon icon={faCalendar} />
            </button>
            <button
                className={`user-v2-detail-stat user-v2-detail-stat-button user-v2-photo-sort-button${sortType === "likes" ? " user-v2-detail-stat-button-active" : ""}`}
                disabled={sortType === "likes"}
                type="button"
                onClick={() => handleSort("likes")}
                aria-label="Sort photos by likes"
            >
                <FontAwesomeIcon icon={faHeart} />
            </button>
        </>
    );

    return (
        <>
            <PageTracking />
            {props.currentUser && (isUiV2 ? <UserDetailsV2 currentUser={props.currentUser} user={user} locations={props.locations} detailActions={v2DetailActions} /> : <UserDetails user={user} />)}
            {user && !isUiV2 && <UserNav currentUser={props.currentUser} user={user} />}
            {isUiV2 ? <V2GlassPanel className="user-v2-photo-gallery-card v2-glass-list-panel">
                {!displayPhotos && <div className="user-v2-empty-list">Loading...</div>}
                {displayPhotos && displayPhotos.length === 0 && <div className="user-v2-empty-list">No photos taken yet</div>}
                {displayPhotos && <div className="v2-hub-photo-row-list">
                    {displayPhotos.map((photo: Photo, index: number) => (
                        <V2PhotoRow
                            key={photo.id + index}
                            currentUser={props.currentUser!}
                            photo={photo}
                            photoUser={undefined}
                            likesMap={props.likesMap}
                            showUser={false}
                            locationPlacement="top"
                            onLikeCountChange={updatePhotoLikeCount}
                        />
                    ))}
                </div>}
            </V2GlassPanel> : <Card className="mb-3">
                {!isUiV2 && photos && photos.length > 0 && <Card.Header className={"header-padding"} style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    {displayPhotos && <div className={"d-inline-block stats-card"}>
                        Photos: <b>{displayPhotos.length}</b>
                    </div>}
                    <div>
                        <Button 
                            className={"btn-sm mr-2 cursor-pointer"} 
                            disabled={sortType === 'date'} 
                            onClick={() => handleSort("date")}
                        >
                            <FontAwesomeIcon icon={faCalendar} />
                        </Button>
                        <Button 
                            className={"btn-sm cursor-pointer"} 
                            disabled={sortType === 'likes'} 
                            onClick={() => handleSort("likes")}
                        >
                            <FontAwesomeIcon icon={faHeart} />
                        </Button>
                    </div>
                </Card.Header>}
                <div>
                    {!displayPhotos && <div className={'loading'}>
                        Loading...
                    </div>}
                    {(displayPhotos && displayPhotos.length === 0) &&<div className={'loading'}>
                        No photos taken yet
                    </div>}
                    <div className={"album"}>
                        {displayPhotos && displayPhotos
                            .map((photo: Photo, index: number) => (
                            props.currentUser &&
                            <PhotoRow
                                key={photo.id + index}
                                currentUser={props.currentUser}
                                photo={photo}
                                photoUser={user}
                                displayLocation={true}
                                displayEditButton={true}
                                likesMap={props.likesMap}
                                onLikeCountChange={updatePhotoLikeCount}
                            />
                        ))}
                    </div>
                </div>
            </Card>}
        </>
    );
}

export default UserPhotos;
